import React, { useState, useEffect, useCallback } from 'react';
import { 
  Upload, Package, FileText, TrendingUp, AlertTriangle, 
  ShoppingCart, Check, X, RefreshCw, ChevronRight, 
  Truck, DollarSign, Clock, BarChart3, Sparkles, 
  Plus, Eye, Send, Edit2, Trash2
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function AIPurchasing() {
  // Dashboard State
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Invoice State
  const [invoices, setInvoices] = useState([]);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  
  // Inventory State
  const [inventory, setInventory] = useState([]);
  const [inventoryStats, setInventoryStats] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  
  // Suppliers State
  const [suppliers, setSuppliers] = useState([]);
  
  // Order Suggestions State
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // Insights State
  const [insights, setInsights] = useState([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/purchasing/dashboard`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data.stats);
      }
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/purchasing/invoices`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      }
    } catch (error) {
      console.error('Invoices fetch error:', error);
    }
  }, []);

  const fetchInventory = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/purchasing/inventory`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInventory(data.items || []);
        setInventoryStats(data.stats);
      }
    } catch (error) {
      console.error('Inventory fetch error:', error);
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/purchasing/suppliers`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data.suppliers || []);
      }
    } catch (error) {
      console.error('Suppliers fetch error:', error);
    }
  }, []);

  const fetchSuggestions = useCallback(async () => {
    setLoadingSuggestions(true);
    try {
      const response = await fetch(`${API_BASE}/api/purchasing/suggestions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Suggestions fetch error:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  const fetchInsights = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/purchasing/insights`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInsights(data.insights || []);
      }
    } catch (error) {
      console.error('Insights fetch error:', error);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchInvoices(),
        fetchInventory(),
        fetchSuppliers(),
        fetchSuggestions(),
        fetchInsights()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchDashboard, fetchInvoices, fetchInventory, fetchSuppliers, fetchSuggestions, fetchInsights]);

  // Invoice Upload Handler
  const handleInvoiceUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingInvoice(true);
    const formData = new FormData();
    formData.append('invoice_file', file);

    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/purchasing/invoices/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Rechnung erfolgreich analysiert!', {
          description: `${data.invoice?.items_count || 0} Positionen erkannt`
        });
        setSelectedInvoice(data);
        setShowInvoiceModal(true);
        await fetchInvoices();
        await fetchInventory();
        await fetchSuppliers();
      } else {
        const error = await response.json();
        toast.error('Fehler beim Hochladen', {
          description: error.detail || 'Bitte versuchen Sie es erneut'
        });
      }
    } catch (error) {
      toast.error('Netzwerkfehler', {
        description: 'Verbindung zum Server fehlgeschlagen'
      });
    } finally {
      setUploadingInvoice(false);
      event.target.value = '';
    }
  };

  // Confirm Invoice
  const confirmInvoice = async (invoiceId) => {
    try {
      const response = await fetch(`${API_BASE}/api/purchasing/invoices/${invoiceId}/confirm`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Rechnung bestätigt!', {
          description: 'Bestand wurde aktualisiert'
        });
        setShowInvoiceModal(false);
        await fetchInvoices();
        await fetchInventory();
        await fetchDashboard();
      }
    } catch (error) {
      toast.error('Fehler beim Bestätigen');
    }
  };

  // Update Inventory Item
  const updateInventoryItem = async (itemId, updates) => {
    try {
      const params = new URLSearchParams(updates);
      const response = await fetch(`${API_BASE}/api/purchasing/inventory/${itemId}?${params}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Artikel aktualisiert');
        setEditingItem(null);
        await fetchInventory();
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  // Send Order
  const sendOrder = async (suggestion) => {
    try {
      const response = await fetch(`${API_BASE}/api/purchasing/orders/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          supplier_id: suggestion.supplier_id,
          items: suggestion.items
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Bestellung versendet!', {
          description: `${data.items_count} Artikel an ${suggestion.supplier_name}`
        });
        await fetchSuggestions();
      }
    } catch (error) {
      toast.error('Fehler beim Versenden');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen" data-testid="purchasing-loading">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-400">Lade KI-Einkaufsleiter...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ai-purchasing-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-purple-400" />
            KI-Einkaufsleiter
          </h1>
          <p className="text-gray-400 mt-1">
            Intelligente Warenwirtschaft ohne klassische Bestandsführung
          </p>
        </div>
        
        {/* Upload Button */}
        <div className="flex items-center gap-3">
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*,application/pdf"
              className="hidden"
              onChange={handleInvoiceUpload}
              disabled={uploadingInvoice}
              data-testid="invoice-upload-input"
            />
            <Button
              disabled={uploadingInvoice}
              className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              data-testid="upload-invoice-btn"
            >
              {uploadingInvoice ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analysiere...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Rechnung hochladen
                </>
              )}
            </Button>
          </label>
        </div>
      </div>

      {/* Stats Cards */}
      {dashboardStats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Lieferanten</p>
                  <p className="text-2xl font-bold text-white">{dashboardStats.suppliers}</p>
                </div>
                <Truck className="w-8 h-8 text-blue-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Lagerartikel</p>
                  <p className="text-2xl font-bold text-white">{dashboardStats.inventory_items}</p>
                </div>
                <Package className="w-8 h-8 text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Offene Rechnungen</p>
                  <p className="text-2xl font-bold text-amber-400">{dashboardStats.pending_invoices}</p>
                </div>
                <FileText className="w-8 h-8 text-amber-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Ausgaben (Monat)</p>
                  <p className="text-2xl font-bold text-white">{dashboardStats.month_spending?.toFixed(2)}€</p>
                </div>
                <DollarSign className="w-8 h-8 text-purple-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-400">Niedriger Bestand</p>
                  <p className="text-2xl font-bold text-red-400">{dashboardStats.low_stock_items}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-800/50 border border-slate-700 p-1">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-blue-600">
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="suggestions" className="data-[state=active]:bg-blue-600">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Bestellvorschläge
            {suggestions.length > 0 && (
              <Badge className="ml-2 bg-red-500">{suggestions.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-blue-600">
            <FileText className="w-4 h-4 mr-2" />
            Rechnungen
          </TabsTrigger>
          <TabsTrigger value="inventory" className="data-[state=active]:bg-blue-600">
            <Package className="w-4 h-4 mr-2" />
            Lager
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="data-[state=active]:bg-blue-600">
            <Truck className="w-4 h-4 mr-2" />
            Lieferanten
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* AI Insights */}
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-400" />
                KI-Insights
              </CardTitle>
              <CardDescription>Automatisch erkannte Muster und Empfehlungen</CardDescription>
            </CardHeader>
            <CardContent>
              {insights.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <TrendingUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Noch keine Insights verfügbar</p>
                  <p className="text-sm">Laden Sie Rechnungen hoch, um KI-Insights zu generieren</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {insights.map((insight, idx) => (
                    <div 
                      key={insight.insight_id || idx}
                      className={`p-4 rounded-lg border ${
                        insight.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                        insight.severity === 'warning' ? 'bg-amber-500/10 border-amber-500/30' :
                        'bg-blue-500/10 border-blue-500/30'
                      }`}
                      data-testid={`insight-${idx}`}
                    >
                      <div className="flex items-start gap-3">
                        {insight.type === 'price_change' && <TrendingUp className="w-5 h-5 text-amber-400 mt-0.5" />}
                        {insight.type === 'slow_mover' && <Clock className="w-5 h-5 text-blue-400 mt-0.5" />}
                        {insight.type === 'margin_warning' && <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />}
                        <div>
                          <h4 className="font-medium text-white">{insight.title}</h4>
                          <p className="text-sm text-gray-400 mt-1">{insight.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 border-purple-500/30 cursor-pointer hover:border-purple-500/50 transition-colors"
                  onClick={() => document.querySelector('[data-testid="invoice-upload-input"]').click()}>
              <CardContent className="pt-6 text-center">
                <Upload className="w-12 h-12 mx-auto mb-3 text-purple-400" />
                <h3 className="font-medium text-white">Rechnung hochladen</h3>
                <p className="text-sm text-gray-400 mt-1">KI extrahiert automatisch alle Daten</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border-green-500/30 cursor-pointer hover:border-green-500/50 transition-colors"
                  onClick={() => setActiveTab('suggestions')}>
              <CardContent className="pt-6 text-center">
                <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-green-400" />
                <h3 className="font-medium text-white">Bestellvorschläge</h3>
                <p className="text-sm text-gray-400 mt-1">
                  {suggestions.length > 0 
                    ? `${suggestions.length} aktive Vorschläge`
                    : 'Automatisch generiert basierend auf Verbrauch'
                  }
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-600/20 to-orange-600/20 border-amber-500/30 cursor-pointer hover:border-amber-500/50 transition-colors"
                  onClick={() => setActiveTab('inventory')}>
              <CardContent className="pt-6 text-center">
                <Package className="w-12 h-12 mx-auto mb-3 text-amber-400" />
                <h3 className="font-medium text-white">Lager verwalten</h3>
                <p className="text-sm text-gray-400 mt-1">Mindestbestände und Warnungen einstellen</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Order Suggestions Tab */}
        <TabsContent value="suggestions" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Bestellvorschläge</h2>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchSuggestions}
              disabled={loadingSuggestions}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingSuggestions ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
          
          {suggestions.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center">
                <Check className="w-16 h-16 mx-auto mb-4 text-green-400 opacity-50" />
                <h3 className="text-lg font-medium text-white">Alles im grünen Bereich!</h3>
                <p className="text-gray-400 mt-2">Keine Bestellungen erforderlich</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {suggestions.map((suggestion, idx) => (
                <Card 
                  key={suggestion.suggestion_id || idx}
                  className={`bg-slate-800/50 border-slate-700 ${
                    suggestion.urgency === 'urgent' ? 'border-l-4 border-l-red-500' : ''
                  }`}
                  data-testid={`suggestion-${idx}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CardTitle className="text-white">{suggestion.supplier_name}</CardTitle>
                        {suggestion.urgency === 'urgent' && (
                          <Badge className="bg-red-500">Dringend</Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-white">{suggestion.total_amount?.toFixed(2)}€</p>
                        <p className="text-xs text-gray-400">{suggestion.item_count} Artikel</p>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Items List */}
                    <div className="space-y-2 mb-4">
                      {suggestion.items?.slice(0, 5).map((item, itemIdx) => (
                        <div key={item.item_id || itemIdx} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            {item.urgency === 'urgent' && (
                              <AlertTriangle className="w-4 h-4 text-red-400" />
                            )}
                            <span className="text-white">{item.name}</span>
                          </div>
                          <div className="flex items-center gap-4 text-gray-400">
                            <span>{item.order_quantity} {item.unit}</span>
                            <span>{item.total_price?.toFixed(2)}€</span>
                          </div>
                        </div>
                      ))}
                      {suggestion.items?.length > 5 && (
                        <p className="text-sm text-gray-400">
                          + {suggestion.items.length - 5} weitere Artikel
                        </p>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-3 pt-4 border-t border-slate-700">
                      <Button 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => sendOrder(suggestion)}
                        data-testid={`send-order-${idx}`}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Bestellung senden
                      </Button>
                      <Button variant="outline">
                        <Edit2 className="w-4 h-4 mr-2" />
                        Bearbeiten
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Rechnungen</h2>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={handleInvoiceUpload}
                disabled={uploadingInvoice}
              />
              <Button disabled={uploadingInvoice}>
                <Plus className="w-4 h-4 mr-2" />
                Neue Rechnung
              </Button>
            </label>
          </div>
          
          {invoices.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-white">Keine Rechnungen</h3>
                <p className="text-gray-400 mt-2">
                  Laden Sie Ihre erste Lieferantenrechnung hoch
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice, idx) => (
                <Card 
                  key={invoice.invoice_id || idx}
                  className="bg-slate-800/50 border-slate-700 cursor-pointer hover:border-slate-600 transition-colors"
                  data-testid={`invoice-${idx}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          invoice.status === 'confirmed' ? 'bg-green-500/20' :
                          invoice.status === 'pending' ? 'bg-amber-500/20' : 'bg-red-500/20'
                        }`}>
                          <FileText className={`w-6 h-6 ${
                            invoice.status === 'confirmed' ? 'text-green-400' :
                            invoice.status === 'pending' ? 'text-amber-400' : 'text-red-400'
                          }`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-white">{invoice.supplier_name}</h3>
                          <p className="text-sm text-gray-400">
                            {invoice.invoice_number || 'Keine Nr.'} • {invoice.invoice_date || 'Kein Datum'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-medium text-white">{invoice.total?.toFixed(2)}€</p>
                          <p className="text-xs text-gray-400">{invoice.items?.length || 0} Positionen</p>
                        </div>
                        <Badge className={
                          invoice.status === 'confirmed' ? 'bg-green-500' :
                          invoice.status === 'pending' ? 'bg-amber-500' : 'bg-red-500'
                        }>
                          {invoice.status === 'confirmed' ? 'Bestätigt' :
                           invoice.status === 'pending' ? 'Offen' : 'Abgelehnt'}
                        </Badge>
                        {invoice.status === 'pending' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => confirmInvoice(invoice.invoice_id)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Lagerbestand</h2>
            {inventoryStats && (
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-400">
                  Gesamtwert: <span className="text-white font-medium">{inventoryStats.total_value?.toFixed(2)}€</span>
                </span>
                {inventoryStats.low_stock_count > 0 && (
                  <Badge className="bg-amber-500">
                    {inventoryStats.low_stock_count} unter Mindestbestand
                  </Badge>
                )}
              </div>
            )}
          </div>
          
          {inventory.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-white">Kein Lagerbestand</h3>
                <p className="text-gray-400 mt-2">
                  Artikel werden automatisch aus Rechnungen angelegt
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {inventory.map((item, idx) => {
                const isLowStock = item.current_stock < item.min_stock_weekday;
                return (
                  <Card 
                    key={item.item_id || idx}
                    className={`bg-slate-800/50 border-slate-700 ${
                      isLowStock ? 'border-l-4 border-l-amber-500' : ''
                    }`}
                    data-testid={`inventory-item-${idx}`}
                  >
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-white">{item.name}</h3>
                            {item.always_in_stock && (
                              <Badge variant="outline" className="text-xs">Pflichtartikel</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-400">
                            {item.supplier_name || 'Kein Lieferant'} • 
                            Letzter EK: {item.last_purchase_price?.toFixed(2)}€/{item.unit}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-6">
                          {/* Current Stock */}
                          <div className="text-center">
                            <p className={`text-lg font-bold ${isLowStock ? 'text-amber-400' : 'text-white'}`}>
                              {item.current_stock} {item.unit}
                            </p>
                            <p className="text-xs text-gray-400">Bestand</p>
                          </div>
                          
                          {/* Min Stock */}
                          <div className="text-center">
                            <p className="text-sm text-gray-400">
                              Min: {item.min_stock_weekday} / {item.min_stock_weekend}
                            </p>
                            <p className="text-xs text-gray-500">Woche / WE</p>
                          </div>
                          
                          {/* Actions */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingItem(item)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Suppliers Tab */}
        <TabsContent value="suppliers" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Lieferanten</h2>
          </div>
          
          {suppliers.length === 0 ? (
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="py-12 text-center">
                <Truck className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-white">Keine Lieferanten</h3>
                <p className="text-gray-400 mt-2">
                  Lieferanten werden automatisch aus Rechnungen erkannt
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {suppliers.map((supplier, idx) => (
                <Card 
                  key={supplier.supplier_id || idx}
                  className="bg-slate-800/50 border-slate-700"
                  data-testid={`supplier-${idx}`}
                >
                  <CardContent className="py-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Truck className="w-6 h-6 text-blue-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium text-white">{supplier.name}</h3>
                        {supplier.email && (
                          <p className="text-sm text-gray-400">{supplier.email}</p>
                        )}
                        {supplier.phone && (
                          <p className="text-sm text-gray-400">{supplier.phone}</p>
                        )}
                        {supplier.order_days?.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            Bestelltage: {supplier.order_days.join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Invoice Preview Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              KI-Rechnungsanalyse
            </DialogTitle>
            <DialogDescription>
              Bitte prüfen Sie die extrahierten Daten
            </DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              {/* Confidence */}
              <div className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                <span className="text-gray-400">KI-Vertrauen</span>
                <div className="flex items-center gap-2">
                  <Progress 
                    value={(selectedInvoice.confidence || 0) * 100} 
                    className="w-32 h-2"
                  />
                  <span className="text-white font-medium">
                    {((selectedInvoice.confidence || 0) * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
              
              {/* Supplier Info */}
              <div className="p-4 bg-slate-800 rounded-lg">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Lieferant</h4>
                <p className="text-white font-medium">{selectedInvoice.supplier?.name}</p>
                {selectedInvoice.supplier?.is_new && (
                  <Badge className="mt-2 bg-green-500">Neu angelegt</Badge>
                )}
              </div>
              
              {/* Invoice Info */}
              <div className="p-4 bg-slate-800 rounded-lg">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Rechnung</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">Nummer</p>
                    <p className="text-white">{selectedInvoice.invoice?.number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Datum</p>
                    <p className="text-white">{selectedInvoice.invoice?.date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Gesamt</p>
                    <p className="text-white font-medium">{selectedInvoice.invoice?.total?.toFixed(2)}€</p>
                  </div>
                </div>
              </div>
              
              {/* Items */}
              <div className="p-4 bg-slate-800 rounded-lg max-h-64 overflow-y-auto">
                <h4 className="text-sm font-medium text-gray-400 mb-3">
                  Positionen ({selectedInvoice.items?.length || 0})
                </h4>
                <div className="space-y-2">
                  {selectedInvoice.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm py-2 border-b border-slate-700 last:border-0">
                      <div>
                        <p className="text-white">{item.name}</p>
                        <p className="text-gray-400">{item.quantity} {item.unit} × {item.unit_price?.toFixed(2)}€</p>
                      </div>
                      <p className="text-white font-medium">{item.total_price?.toFixed(2)}€</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Warnings */}
              {selectedInvoice.warnings?.length > 0 && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <h4 className="text-sm font-medium text-amber-400 mb-2">Hinweise</h4>
                  <ul className="text-sm text-amber-300 list-disc list-inside">
                    {selectedInvoice.warnings.map((warning, idx) => (
                      <li key={idx}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>
              Schließen
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => confirmInvoice(selectedInvoice?.invoice_id)}
            >
              <Check className="w-4 h-4 mr-2" />
              Bestätigen & Bestand aktualisieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Inventory Item Modal */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Artikel bearbeiten</DialogTitle>
            <DialogDescription>{editingItem?.name}</DialogDescription>
          </DialogHeader>
          
          {editingItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Mindestbestand (Wochentag)</Label>
                  <Input
                    type="number"
                    defaultValue={editingItem.min_stock_weekday}
                    id="min_stock_weekday"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mindestbestand (Wochenende)</Label>
                  <Input
                    type="number"
                    defaultValue={editingItem.min_stock_weekend}
                    id="min_stock_weekend"
                    className="bg-slate-800 border-slate-700"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Aktueller Bestand</Label>
                <Input
                  type="number"
                  defaultValue={editingItem.current_stock}
                  id="current_stock"
                  className="bg-slate-800 border-slate-700"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Immer auf Lager</Label>
                <Switch
                  defaultChecked={editingItem.always_in_stock}
                  id="always_in_stock"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingItem(null)}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => {
                const updates = {
                  min_stock_weekday: document.getElementById('min_stock_weekday').value,
                  min_stock_weekend: document.getElementById('min_stock_weekend').value,
                  current_stock: document.getElementById('current_stock').value,
                  always_in_stock: document.getElementById('always_in_stock').checked
                };
                updateInventoryItem(editingItem.item_id, updates);
              }}
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
